import { Injectable } from '@angular/core';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom, tap, debounceTime } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { WidgetsActions } from '@actions/index'
import * as fromApp from '@reducers/index'
import { HttpApiService } from '@services/http-api.service';
import { ReviewModel, RequestModel, LocationModel, ReviewPageRequestDescriptor } from '@models/index';


/**
 * WidgetsEffects handles all effects related
 * to widgets and dispatches the corrspondent
 * actions or services.
 */
@Injectable()
export class WidgetsEffects {

    /**
     * Pipes all [Widgets Page] Filter Reviews By Location
     * actions and peroforms a HTTP petition to get
     * reviews on a given location that matches
     * the following widget settings. When the petition
     * is succesfull it dispatches the [Widgets Page] 
     * Search Reviews Succes action to store the value on the
     * app state. If the petition returns an error the effect
     * dispatches [Widgets Page] Widgets Error to store the
     * error on widget's app state.
     */
    searchReviewsByLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsActions.filterReviewsByLocation),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.postRequest(request)
                    .pipe(
                        map((data) => data as { data: ReviewModel[], total: number }),
                        map(data => WidgetsActions.searchWidgetReviewsSuccess({ ...data })),
                        catchError(error => of(WidgetsActions.widgetError({ error: error.message })))
                    )
            )
        )
    );
    /**
     * Pipes all [Widgets Page] Get Widget Url
     * actions and peroforms a HTTP petition to get
     * the assigned widget URL that saves
     * widget's settings. When the petition
     * is succesfull it dispatches the [Widgets Page] 
     * Get Widget Url Success action to store the
     * value on the app state.
     * If the petition returns an error the effect
     * dispatches [Widgets Page] Widgets Error to store the
     * error on widget's app state.
     */
    getWidgetUrl$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsActions.getWidgetUrl),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.postRequest(request)
                    .pipe(
                        map((data: { key: string }) => data.key as string),
                        map(widgetUrl => WidgetsActions.getWidgetUrlSuccess({ widgetUrl })),
                        catchError(error => of(WidgetsActions.widgetError({ error: error.message })))
                    )
            )
        )
    );
    /**
     * Pipes all [Widgets Page] Get Location Reviews Count
     * actions and peroforms a HTTP petition to get
     * selected location reviews total ammount. 
     * When the petition is succesfull it dispatches 
     * the [Widgets Page] Get Location Reviews Count
     * Success action to store the value on the app state.
     * If the petition returns an error the effect
     * dispatches [Widgets Page] Widgets Error to store the
     * error on widget's app state.
     */
    getLoctionReviewsCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsActions.getLoctionReviewsCount),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getCurrentLocation),
                (request: RequestModel, location: LocationModel) => {
                    return ({
                        ...request,
                        params: request.params + `${location.id}`
                    })
                }
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: { size: number }) => data.size),
                        map(reviewsCount => WidgetsActions.getLoctionReviewsCountSuccess({ reviewsCount })),
                        catchError(error => of(WidgetsActions.widgetError({ error: error.message })))
                    )
            )
        )
    );

    /**
     * Pipes all [Widgets Page] Get Location Overall Rating
     * actions and peroforms a HTTP petition to get the
     * selected location overall reviews rating. 
     * When the petition is succesfull it dispatches 
     * the [Widgets Page] Get Location Overall Rating Success
     * action to store the value on the app state.
     * If the petition returns an error the effect
     * dispatches [Widgets Page] Widgets Error to store the
     * error on widget's app state.
     */
    getLoctionOverallRating$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsActions.getLoctionOverallRating),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getCurrentLocation),
                (request: RequestModel, location: LocationModel) => {
                    return ({
                        ...request,
                        params: request.params + `locationId=${location.id}`
                    })
                }
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: number) => data),
                        map(rating => WidgetsActions.getLoctionOverallRatingSuccess({ rating })),
                        catchError(error => of(WidgetsActions.widgetError({ error: error.message })))
                    )
            )
        )
    );

    /**
     * Pipes all [Widgets Page] Get Location Stars Count
     * actions and peroforms a HTTP petition to get the 
     * selected location reviews' stars distribution. 
     * When the petition is succesfull it dispatches 
     * the [Widgets Page] Get Location Stars Count Success
     * action to store the value on the app state.
     * If the petition returns an error the effect
     * dispatches [Widgets Page] Widgets Error to store the
     * error on widget's app state.
     */
    getLoctionStarsCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsActions.getLoctionStarsCount),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getCurrentLocation),
                (request: RequestModel, location: LocationModel) => {
                    return ({
                        ...request,
                        params: request.params + `locationId=${location.id}`
                    })
                }
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: { data: number[] }) => data.data),
                        map(starsCount => WidgetsActions.getLoctionStarsCountSuccess({ starsCount })),
                        catchError(error => of(WidgetsActions.widgetError({ error: error.message })))
                    )
            )
        )
    );

    /**
     * Pipes all [Widgets Page] Get Location Generic Landing
     * actions and peroforms a HTTP petition to get the 
     * selected location generic landing page URL. 
     * When the petition is succesfull it dispatches 
     * the [Widgets Page] Get Location Generic Landing Success
     * action to store the value on the app state.
     * If the petition returns an error the effect
     * dispatches [Widgets Page] Widgets Error to store the
     * error on widget's app state.
     */
    getLoctionGenericLanding$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsActions.getLoctionGenericLanding),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getCurrentLocation),
                (request: RequestModel, location: LocationModel) => {
                    return ({
                        ...request,
                        params: request.params + `locationId=${location.id}`
                    })
                }
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: { data: string }) => data.data),
                        map(landing => WidgetsActions.getLoctionGenericLandingSuccess({ landing })),
                        catchError(error => of(WidgetsActions.widgetError({ error: error.message })))
                    )
            )
        )
    );

    getPagesId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WidgetsActions.getPagesIdWidgets),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.postRequest(request).pipe(
                    map((data) => data as ReviewPageRequestDescriptor[]),
                    map(data => WidgetsActions.getPagesIdWidgetsSuccess({ data })),
                    catchError(error => of(WidgetsActions.widgetError({ error: error.message }))),
                )
            ),
        )
    );

    constructor(
        private actions$: Actions,
        private httpApiService: HttpApiService,
        private store: Store<any>
    ) { }
}
